import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import { useReveal } from "../lib/useReveal";

const pillars = [
  { tag: "SPIRIT", words: "Faith · Prayer · Worship", n: "01" },
  { tag: "MIND", words: "Wisdom · Study · Awareness", n: "02" },
  { tag: "BODY", words: "Discipline · Care · Strength", n: "03" },
];

export default function Founder() {
  useReveal();

  return (
    <main data-testid="founder-page" className="bg-[#F4EBDC]">
      {/* HEADER */}
      <section
        data-testid="founder-header"
        className="pt-32 sm:pt-40 pb-16 bg-[#F4EBDC]"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6">
            <span className="tag" data-testid="founder-eyebrow">
              FOUNDER · VESSEL · 02
            </span>
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#0E0E0E]/70">
              EST. BY FAITH · LOS ANGELES
            </span>
          </div>
          <span className="tape" />

          <h1
            className="font-display reveal reveal-delay-1 mt-8 text-[14vw] sm:text-[10vw] lg:text-[7vw] xl:text-[150px] text-[#0E0E0E] leading-[0.9]"
            data-testid="founder-title"
          >
            TRAYSHUN D.
            <br />
            <span className="text-[#1B4F72]">RICHMOND</span>
          </h1>
          <p className="reveal reveal-delay-2 mt-8 text-base sm:text-lg max-w-2xl text-[#0E0E0E]/85 leading-relaxed">
            A servant of God called to protect, nurture, and guide the pure
            hearts of youth with faith, love, and purpose.
          </p>
        </div>
      </section>

      {/* BIO SPLIT */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 reveal relative">
            <div className="photo-frame">
              <img
                src="/assets/founder/trey-wideshot.png"
                alt="Trayshun D. Richmond"
                className="w-full object-cover aspect-[4/5] photo-duotone"
                style={{ objectPosition: "center 30%" }}
                data-testid="founder-image"
              />
            </div>
            <span className="sticker sticker--ink absolute -bottom-4 -right-3">
              EST. BY FAITH
            </span>
          </div>

          <div className="lg:col-span-7 reveal reveal-delay-1">
            <span className="tag">/ BIOGRAPHY</span>
            <h2 className="font-display mt-5 text-[10vw] sm:text-[6vw] lg:text-[3.6vw] text-[#0E0E0E] leading-[0.95]">
              THE WORK IS A
              <br />
              <span className="text-[#1B4F72]">MISSION FROM</span>
              <br />
              THE LORD.
            </h2>
            <div className="mt-8 space-y-5 text-[#0E0E0E]/85 text-base sm:text-lg leading-relaxed">
              <p>
                Trayshun D. Richmond is a servant of God and the founder of
                Divine Care Organization. He believes this work is a mission
                from the Lord. Through Divine Care Organization, Mr. Richmond
                seeks to protect, nurture, and guide the pure hearts of youth
                with faith, love, and purpose.
              </p>
              <p>
                He understands the importance of helping young people grow
                spiritually, mentally, physically, and emotionally so they can
                become all God has called them to be.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {pillars.map((p) => (
                <div
                  key={p.tag}
                  className="panel-flat p-5 bg-[#F4EBDC] border-2 border-[#0E0E0E]"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="big-num text-[3.5rem] leading-none">
                      {p.n}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.22em]">
                      / PILLAR
                    </span>
                  </div>
                  <p className="font-display text-2xl mt-2 text-[#0E0E0E]">
                    {p.tag}
                  </p>
                  <p className="font-mono text-[11px] tracking-[0.18em] uppercase mt-2 text-[#0E0E0E]/70">
                    {p.words}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section
        data-testid="founder-quote"
        className="bg-[#0E0E0E] text-[#F4EBDC] py-28 sm:py-40 relative overflow-hidden"
      >
        <div className="absolute -top-10 -left-10 opacity-20 text-[#FFD700]">
          <Quote size={300} strokeWidth={1.5} />
        </div>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10 text-center relative">
          <span className="font-mono text-[11px] tracking-[0.22em] text-[#FFD700] reveal">
            FOUNDER'S WORD / 01
          </span>
          <blockquote className="mt-8 reveal reveal-delay-1">
            <p className="font-display italic text-[10vw] sm:text-[6.5vw] lg:text-[5vw] xl:text-[80px] leading-[1.02] text-[#F4EBDC]">
              “LIFE IS LIKE A GARDEN, SO WE MUST TEND TO IT WELL, NURTURE IT
              WELL, AND <span className="text-[#FFD700]">CARE FOR IT</span>{" "}
              INTENTLY.”
            </p>
            <footer className="mt-10 font-mono text-xs tracking-[0.22em] text-[#FFD700]">
              — T. RICHMOND
            </footer>
          </blockquote>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20 bg-[#F4EBDC] border-t-2 border-[#0E0E0E]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 reveal">
          <div>
            <span className="tag">/ KEEP GOING</span>
            <h3 className="font-display mt-3 text-4xl sm:text-5xl text-[#0E0E0E]">
              WALK THROUGH THE PROGRAMS WE'RE BUILDING.
            </h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/basketball-camp"
              className="btn-base btn-orange"
              data-testid="founder-cta-camp"
            >
              Basketball Camp <ArrowUpRight size={14} />
            </Link>
            <a
              href="/#contact"
              className="btn-base btn-ghost"
              data-testid="founder-cta-contact"
            >
              Contact
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
